import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * Heuristic password feedback: a **rules** table drives both a checklist (`<li class="ok">`) and a meter
 * (`width` + discrete background colors). Replace rules with zxcvbn or server checks in production.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'password-strength',
  category: 'forms-flow',
  title: 'Password strength',
  description: 'Meter + checklist updates live from heuristics.',
  tags: [T.form, T.password, T.validation],
  html: `<h2>Password</h2>

<!-- Field: type=password keeps input masked; JS reads .value on each input event. -->
<label class="lbl">Password
  <input type="password" id="pw" autocomplete="off" />
</label>

<!-- Track + inner fill: width and background are updated in JS from rule count. -->
<div class="meter"><div class="fill" id="mf"></div></div>

<!-- Empty in markup; list items are rebuilt in JS so each row can toggle .ok + glyph. -->
<ul id="chk" class="chk"></ul>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 0.75rem;
}

/* Stacked label + control */
.lbl {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-width: 18rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--nogrok-preview-on-surface-variant);
}

input {
  font: inherit;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--nogrok-preview-outline);
  background: var(--nogrok-preview-surface-container);
  color: var(--nogrok-preview-on-surface);
}

/* Background track; .fill is the only moving part */
.meter {
  height: 6px;
  border-radius: 3px;
  background: var(--nogrok-preview-surface-container-high);
  max-width: 18rem;
  overflow: hidden;
  margin-top: 0.75rem;
}

/* Width 0–100% = share of rules passed; color steps from weak → strong in JS */
.meter .fill {
  height: 100%;
  width: 0%;
  background: #ff4d4d;
  transition: width 0.15s ease, background 0.15s ease;
}

.chk {
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.8rem;
}

/* Dim until the matching rule passes */
.chk li {
  margin: 4px 0;
  opacity: 0.45;
}

.chk li.ok {
  opacity: 1;
  color: #7dffb0;
}

/* Icons via generated content — swap ✗ / ✓ when .ok is present */
.chk li::before {
  content: '✗ ';
  color: #ff8a8a;
}

.chk li.ok::before {
  content: '✓ ';
  color: #7dffb0;
}`,
  js: `const pw = document.getElementById('pw')
const mf = document.getElementById('mf')
const chk = document.getElementById('chk')

// Each rule: label for the list + test(password) → boolean (demo heuristics only).
const rules = [
  { id: 'len', label: '8+ characters', test: (s) => s.length >= 8 },
  { id: 'mix', label: 'Letter + number', test: (s) => /[a-z]/i.test(s) && /\\d/.test(s) },
  { id: 'sym', label: 'Symbol', test: (s) => /[^a-z0-9]/i.test(s) },
]

function paint() {
  const s = pw instanceof HTMLInputElement ? pw.value : ''
  let n = 0
  if (!chk) return

  // Rebuild <li> each time so class "ok" stays in sync with rules (simple for a tiny demo).
  chk.innerHTML = ''
  for (const r of rules) {
    const ok = r.test(s)
    if (ok) n++
    const li = document.createElement('li')
    li.className = ok ? 'ok' : ''
    li.textContent = r.label
    chk.appendChild(li)
  }

  const pct = (n / rules.length) * 100
  if (mf) {
    mf.style.width = pct + '%'
    // Discrete colors by how many rules pass (0 = empty/gray → green when all pass).
    mf.style.background = n === 0 ? 'var(--nogrok-preview-surface-container-high)' : n === 1 ? '#ff4d4d' : n === 2 ? 'var(--nogrok-preview-accent-warm)' : '#7dffb0'
  }
}

pw?.addEventListener('input', paint)
paint()`,
}

export default preset
